import { VolumeX } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  LONG_SILENCE_SECONDS,
  MIN_SILENT_STRETCH_SECONDS,
  formatCoverageSeconds,
  isUnderNarrated,
  longestSilenceLabel,
  readNarrationCoverage,
  sceneLabel,
  type NarrationCoverage,
  type NarrationCoverageStretch,
} from "./narrationCoverage";

const SOURCE_COPY: Record<NarrationCoverage["source"], string> = {
  manifest: "measured by the composer",
  computed: "computed from the narration timings",
};

/**
 * "Longest silence: 10.5 s (scenes 7–10)" (RNC-014): where the delivered film
 * has picture but no voice, so an operator can spot an under-narrated Echo.
 * Title cards and the outro are exempt. Stretches of 6 s or more are listed
 * and highlighted; shorter pauses (from 2 s) fold away. Renders nothing for
 * an Echo with no realized narration or a manifest from another render.
 */
export function NarrationCoveragePanel({
  manifest,
  onSeek,
  workloadId,
  finalVideoKey,
}: {
  manifest: unknown;
  /** Seek the player to a delivered-file second. */
  onSeek?: (seconds: number) => void;
  workloadId?: string | null;
  finalVideoKey?: string | null;
}) {
  const coverage = useMemo(
    () => readNarrationCoverage(manifest, { workloadId, finalVideoKey }),
    [manifest, workloadId, finalVideoKey],
  );
  if (!coverage) return null;

  const long = coverage.stretches.filter(
    (stretch) => stretch.seconds >= LONG_SILENCE_SECONDS,
  );
  const short = coverage.stretches.filter(
    (stretch) => stretch.seconds < LONG_SILENCE_SECONDS,
  );
  const underNarrated = isUnderNarrated(coverage);

  return (
    <div
      className={cn(
        "mt-3 space-y-2 rounded border p-3 text-xs",
        underNarrated && "border-amber-300",
      )}
      data-testid="narration-coverage"
      data-source={coverage.source}
    >
      <div className="flex flex-wrap items-center gap-2">
        <VolumeX className="size-3.5" aria-hidden="true" />
        <span className="font-medium" data-testid="narration-longest-silence">
          {longestSilenceLabel(coverage)}
        </span>
        {underNarrated ? (
          <Badge
            variant="outline"
            className="border-amber-300 font-normal text-amber-700 dark:text-amber-400"
          >
            under-narrated
          </Badge>
        ) : null}
        <span className="text-muted-foreground">
          Speech {formatCoverageSeconds(coverage.speechSeconds)} over{" "}
          {formatCoverageSeconds(coverage.pictureSeconds)} of picture ·{" "}
          {SOURCE_COPY[coverage.source]}
        </span>
      </div>

      {long.length > 0 ? (
        <ul className="space-y-1">
          {long.map((stretch) => (
            <li key={stretch.start}>
              <StretchRow
                stretch={stretch}
                coverage={coverage}
                onSeek={onSeek}
                highlight
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">
          No silent stretch of {LONG_SILENCE_SECONDS} s or more over picture.
        </p>
      )}

      {short.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            {short.length} shorter {short.length === 1 ? "silence" : "silences"}{" "}
            ({MIN_SILENT_STRETCH_SECONDS}–{LONG_SILENCE_SECONDS} s)
          </summary>
          <ul className="mt-1 space-y-1">
            {short.map((stretch) => (
              <li key={stretch.start}>
                <StretchRow
                  stretch={stretch}
                  coverage={coverage}
                  onSeek={onSeek}
                />
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <p className="text-muted-foreground">
        Title cards and the outro are exempt · times refer to the delivered
        video.
      </p>
    </div>
  );
}

function StretchRow({
  stretch,
  coverage,
  onSeek,
  highlight = false,
}: {
  stretch: NarrationCoverageStretch;
  coverage: NarrationCoverage;
  onSeek?: (seconds: number) => void;
  highlight?: boolean;
}) {
  const start = stretch.start + coverage.deliveredOffsetSeconds;
  const end = stretch.end + coverage.deliveredOffsetSeconds;
  const scenes = sceneLabel(stretch.sceneIds);
  const text = `${start.toFixed(2)}–${end.toFixed(2)}s · ${formatCoverageSeconds(
    stretch.seconds,
  )} silent${scenes ? ` · ${scenes}` : ""}`;
  const className = cn(
    "block text-left",
    highlight && "text-amber-700 dark:text-amber-400",
  );
  if (!onSeek) {
    return (
      <span className={className} data-testid="narration-silence-stretch">
        {text}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cn(className, "underline")}
      onClick={() => onSeek(start)}
      data-testid="narration-silence-stretch"
    >
      {text}
    </button>
  );
}
